import { combineLatest, firstValueFrom } from 'rxjs';
import { CBooking, IBookingItem, IUpcomingBookingDetails } from '@app/tickets/tickets.model';
import { DateFormatService } from '@core/date/date-format.service';
import { LoggerService } from '@core/logger/logger.service';
import { ProductHelperService } from '@core/product/product-helper.service';
import { ProductService } from '@core/product/product.service';
import { VenueConfigHelperService } from '@core/venue-config/venue-config-helper.service';
import { WhenAttendingSessionService } from '@app/when-attending/when-attending-session/when-attending-session.service';
import { IProduct } from '@core/product/models/product.model';
import {
  linkProductAndParentProduct,
  linkRelatedBookingItems,
  sortBookingItems
} from '@app/tickets/ticket-helper.util';
import { COpenProducts, TProductType } from '@core/product/models/product-type.type';
import { IProductInfo } from '@core/product/models/product-info.model';
import { IProductSession, TWhenAttendingProductSession } from '@core/product/models/product-session.model';
import { hexGenerate } from '@roller/rdl/utilities/string';
import { IOrderItem } from '@core/order/models/order-item.model';
import { OrderItemService } from '@core/order/order-item.service';
import { INumberIncrementProductItem } from '@core/form/number-increment/number-increment-product-item.model';
import { Injectable } from '@angular/core';
import { ProductOrderingService } from '@app/products/product/product-ordering/product-ordering.service';
import { IOrderAmounts } from '@core/order/models/order.model';
import { EPaymentTypes } from '@core/payment/enums/payment-types.enum';
import { IGiftcardCanDeduct } from '@core/gift-card/gift-card-can-deduct.model';
import { IDraftBookingCreated } from '@core/booking/models/draft-booking-created.model';
import { IOrderItemGroupItem } from '@core/order/models/order-item-group.model';
import { IOptionSetSelection } from '../checkout/model/checkout-option-set-selection.model';

/**
 * This service is responsible for handling managing orders created from an existing booking
 */
@Injectable({
  providedIn: 'root'
})
export class OrderExistingBookingService {
  constructor(
    private orderItemService: OrderItemService,
    private dateFormatService: DateFormatService,
    private loggerService: LoggerService,
    private productHelperService: ProductHelperService,
    private productService: ProductService,
    private productOrderingService: ProductOrderingService,
    private venueConfigHelperService: VenueConfigHelperService,
    private whenAttendingSessionService: WhenAttendingSessionService
  ) {}

  /** If adding not allowed returns only the difference between original and new qty */
  getEditBookingAmountRemaining(basketItem: IOrderItem | undefined, amountRemaining: number): number {
    if (basketItem?.existingBookingItem && !basketItem.existingBookingItem.canAddItems) {
      return basketItem.existingBookingItem.quantity - basketItem.quantity;
    }

    return amountRemaining;
  }

  /** Returns 0 or quantity from the original booking if it is not allowed to remove items from the booking  */
  getEditBookingItemMin(
    basketItem: IOrderItem | undefined,
    isNotRemovable: boolean,
    minPurchase: number | undefined
  ): number {
    if (basketItem?.existingBookingItem && !basketItem.existingBookingItem.canRemoveItems) {
      return basketItem.existingBookingItem.quantity;
    } else if (isNotRemovable) {
      return minPurchase || 1;
    }

    return 0;
  }

  /** All related items of the same session or date should be changed together when a item is rescheduled. */
  getRelatedRescheduleItems(orderItems: IOrderItem[], editItem?: IOrderItem): IOrderItem[] {
    return orderItems.filter((oi) => {
      if (oi.product.isItemSessionBased && !this.productHelperService.isSameSession(oi.session, editItem?.session)) {
        return false;
      }

      if (oi.product.isItemDateBased && !this.dateFormatService.isSameDay(oi.bookingDate, editItem?.bookingDate)) {
        return false;
      }

      return oi.relatedOrderItemKey === this.productOrderingService.getRelatedBasketKey();
    });
  }

  /**
   * The draft booking contains important information in post-checkout workflows for signup, customerId, bookingId etc so we construct it for the waiver workflow
   */
  getDraftFromBooking(booking: IUpcomingBookingDetails): IDraftBookingCreated {
    return {
      bookingTimerId: '',
      uniqueBookingId: booking.bookingUniqueId,
      amountOwing: booking.amountOwing || 0,
      paymentJwt: '',
      hasRecurringBilling: booking.hasRecurringBilling || false,
      hasOnlineAccount: booking.hasOnlineAccount || false,
      customerUniqueId: booking.customerUniqueId,
      bookingCosts: {
        total: booking.grandTotal,
        totalIgnoringDeposit: booking.totalIgnoringDeposit || 0,
        totalExcludingFees: booking.subTotal,
        amountOwing: booking.amountOwing || 0,
        tax: booking.tax,
        taxExclusive: booking.taxExclusive || 0,
        transactionFee: booking.transactionFee,
        cardFee: booking.creditCardFee,
        discount: booking.discount
      }
    };
  }

  /**
   * We have to create a giftCard from payments to display it in the summary.
   * We only need the number and amount deducted the rest we can just fill out to satisfy the model, as we won't use it for application
   */
  getGiftCardsFromBooking(booking: IUpcomingBookingDetails): IGiftcardCanDeduct[] {
    return booking.payments
      .filter((p) => p.paymentType === EPaymentTypes.Giftcard)
      .map((payment) => {
        return <IGiftcardCanDeduct>{
          giftcardId: payment.giftcardNumber || 0,
          giftcardNumber: payment.giftcardNumber,
          amountDeducted: payment.total,
          amountRemaining: 0,
          giftcardValue: 0,
          isFree: false,
          needsMoreInfo: false
        };
      });
  }

  /** When we hydrate a completed booking to our order instance we want to map as many values available to the order amounts */
  getAmountsFromExistingBooking(booking: IUpcomingBookingDetails): IOrderAmounts {
    return {
      creditCardFees: booking.creditCardFee,
      deposit: booking.deposit ?? booking.subTotal,
      amountIfDepositOnly: booking.deposit ?? booking.subTotal,
      depositIncFees: booking.grandTotal,
      discount: booking.discount,
      fees: booking.transactionFee,
      feesTax: booking.feeTax,
      giftCard: booking.giftcardDeducted || 0,
      paymentAmountIncCardFees: booking.grandTotal,
      postage: booking.postage,
      subTotal: booking.subTotal,
      subTotalTax: booking.tax - booking.feeTax,
      tax: booking.tax,
      taxExclusive: booking.taxExclusive || 0,
      total: booking.deposit
        ? booking.deposit + (booking.depositFee || 0)
        : booking.grandTotal - (booking.giftcardDeducted || 0),
      totalExcCardFees: booking.grandTotal - booking.creditCardFee,
      totalIgnoringDeposit: booking.totalIgnoringDeposit || 0,
      transactionFees: booking.transactionFee,
      paid: booking.amountPaidExcludingCardFees + booking.creditCardFee + booking.transactionFee,
      paidExcludingFees: booking.amountPaidExcludingCardFees
    };
  }

  /** Builds a checkout basket from an existing booking for order editing */
  async getOrderItemsFromExistingBooking(booking: IUpcomingBookingDetails): Promise<IOrderItem[]> {
    const products: IProduct[] = await this.loadProductWithAvailabilities(booking);
    const bookingItems = booking.bookingItems
      .map((item: IBookingItem) => {
        const parentProduct = products.find((p) => p.id === item.parentProductId.toString());
        item.product = parentProduct?.products.find(
          (product: IProductInfo) => product.id === item.productId.toString()
        );

        return item;
      })
      .sort((itemA: IBookingItem, itemB: IBookingItem) => sortBookingItems(itemA, itemB));

    const orderItems: IOrderItem[] = [];

    bookingItems
      .filter((item: IBookingItem) => !(item.isPackageInclusion || item.isPartyPackageInclusion))
      .forEach((item: IBookingItem) => {
        const parentProduct = products.find((p) => p.id === item.parentProductId.toString());
        const orderItem: IOrderItem | undefined = this.createOrderItemFromBookingItem(
          booking,
          item,
          orderItems,
          parentProduct
        );
        if (orderItem) {
          orderItems.push({
            ...orderItem,
            existingBookingItem: item,
            manageBookingType: CBooking
          });
        }
      });

    return this.mergeSeparateItemsForPackages(orderItems);
  }

  /**
   * Returns booking items converted to order items ready for display in order summary.
   * The reason we use this over getOrderItemsFromExistingBooking is that it's used for display,
   * so can happen sync and as such doesn't need to load any product availability
   */
  getOrderSummaryItemsFromBooking(booking: IUpcomingBookingDetails, products: IProduct[]): IOrderItem[] {
    const bookingItems: IBookingItem[] = booking.bookingItems
      .map((item: IBookingItem) => linkProductAndParentProduct(item, products))
      .map((item: IBookingItem, _: number, list: IBookingItem[]) => linkRelatedBookingItems(item, list))
      .sort((itemA: IBookingItem, itemB: IBookingItem) => sortBookingItems(itemA, itemB));

    const hasPartyPackageInclusions = bookingItems.some((item: IBookingItem) => item.isPartyPackageInclusion);
    if (hasPartyPackageInclusions) {
      const partyParents = bookingItems.filter((bi: IBookingItem) => bi.product?.type === 'partypackage');

      partyParents.forEach((partyParent) => {
        partyParent.packageBookingItems = bookingItems.filter(
          (bi: IBookingItem) => bi.isPartyPackageInclusion && bi.packageBookingItemId === partyParent.bookingItemId
        );
      });
    }

    const hasPackageInclusions = bookingItems.some((item: IBookingItem) => item.isPackageInclusion);
    if (hasPackageInclusions) {
      const packageParents = bookingItems.filter((bi: IBookingItem) => bi.product?.type === 'package');

      packageParents.forEach((packageParent) => {
        packageParent.packageBookingItems = bookingItems.filter(
          (bi: IBookingItem) => bi.isPackageInclusion && bi.packageBookingItemId === packageParent.bookingItemId
        );
      });
    }

    return bookingItems.flatMap((item) => {
      const parentProduct = products.find((product) => product.id === item.parentProductId.toString());
      if (item.isPackageInclusion || item.isPartyPackageInclusion || !parentProduct) {
        return [];
      }

      const productType: TProductType = this.productHelperService.convertToCxProductType(
        item.productTypeId,
        item.productSubTypeId
      );

      const orderItem = this.mapBookingItemToOrderItem(item, productType, parentProduct);
      if (item.packageBookingItems?.length) {
        orderItem.packageOrderItems = item.packageBookingItems.flatMap((packageItem) => {
          const packageParentProduct = products.find(
            (product) => product.id === packageItem.parentProductId.toString()
          );
          if (!packageParentProduct) {
            return [];
          }

          const packageItemProductType: TProductType = this.productHelperService.convertToCxProductType(
            packageItem.productTypeId,
            packageItem.productSubTypeId
          );
          return [this.mapBookingItemToOrderItem(packageItem, packageItemProductType, packageParentProduct)];
        });

        // NOTE: This will only support single session variations - add the additional guests to the primary package
        orderItem.additionalGuests =
          orderItem.packageOrderItems.find((item) => item.productType === 'sessionpass')?.additionalGuests || 0;
        orderItem.ticketQuantity =
          orderItem.packageOrderItems.find((item) => item.productType === 'sessionpass')?.ticketQuantity ??
          orderItem.ticketQuantity;
      }

      return [orderItem];
    });
  }

  isRescheduleAddonSelectionRequired(items: IOrderItemGroupItem[]): boolean {
    const openProductsForReschedule = items.filter(
      (item) => item.basketGroupTitle === COpenProducts && item.existingBookingItem?.canReschedule
    );

    const uniqueSessions = new Set(
      items.filter((item: IOrderItem) => item.session).map((item: IOrderItem) => JSON.stringify(item.session))
    );

    if (uniqueSessions.size === 1) {
      openProductsForReschedule.forEach((item: IOrderItem) => (item.isRescheduling = true));
    }

    return !!openProductsForReschedule.length && uniqueSessions.size > 1;
  }

  /**
   * Maps booking items to order group - used in displaying order grouping summary in the booking details
   * At this point parent product has been linked to the booking item or filtered out, so it's safe to non-null it
   */
  private mapBookingItemToOrderItem(
    item: IBookingItem,
    productType: TProductType,
    parentProduct: IProduct
  ): IOrderItem {
    const product = this.mapBookingItemProduct(item, productType);
    const ticketQuantity = item.quantity * product.groupSize + item.additionalGuests;
    return {
      parentName: parentProduct.name,
      parentMinPurchase: parentProduct.minPurchase,
      uniqueId: String(item.bookingItemId),
      bookingDate: this.dateFormatService.dateStringToDate(item.bookingDate),
      cost: item.total,
      parentProductId: String(item.parentProductId),
      product: product,
      productId: String(item.productId),
      productType: productType,
      quantity: item.quantity,
      session: this.mapBookingItemSession(item),
      hideSessionDuration: item.hideSessionDuration,
      comments: item.comments,
      relatedOrderItemKey: item.relatedOrderItemKey || hexGenerate(15),
      addonToProductId: item.addonToProductId,
      addonList: [],
      orderSelections: [],
      optionSetSelections: [],
      discount: 0,
      groupSize: product.groupSize,
      tax: 0,
      costPercentageRequired: 0,
      manageBookingType: CBooking,
      isPerGuestItem: parentProduct.durationType === 'perGuest',
      isLinkedToConqueror: this.productHelperService.isLinkedToConqueror(parentProduct),
      parentMaxPurchase: parentProduct.maxPurchase,
      additionalGuests: item.additionalGuests,
      ticketQuantity: ticketQuantity,
      isUserDefinedGroupSize: this.productHelperService.isUserDefinedGroupSize(product),
      isFixedPackageItem: (item.isPackageInclusion || item.isPartyPackageInclusion) && !item.isGuestSelection
    };
  }

  /** Minimum product details required, most hard set to display correct in the order summary */
  private mapBookingItemProduct(item: IBookingItem, productType: TProductType): IProductInfo {
    return {
      ...(item.product ?? {}),
      cost: item.productCost,
      description: item.description,
      id: String(item.productId),
      imageUrl: item.imageUrl,
      name: item.name,
      type: productType,
      fee: 0, // used only for basket
      forceMinPurchase: false, // used only for basket
      groupSize: item.product?.groupSize ?? 1,
      hasUserDefinedCost: false, // used only for basket
      isTaxInclusive: true, // used only for basket
      locations: [], // used only for basket
      minPurchase: 0, // used only for basket
      productCategoryIds: [], // used only for basket
      tax: 0, // used only for basket
      maxAdditionalGuests: 0
    };
  }

  private mapBookingItemSession(item: IBookingItem): IProductSession {
    return {
      allocations: [],
      date: item.bookingDate,
      endTime: item.sessionEnd || '',
      name: item.sessionName || '',
      onlineSalesOpen: false,
      startTime: item.sessionStart || ''
    };
  }

  /** Merges product availabilities to get all the sessions for the booking. */
  private mergeProducts(availabilityList: IProduct[][]): IProduct[] {
    const products = this.productService.getProductListSync();
    const uniqueProducts = new Map(products.map((product) => [product.id, { ...product }]));

    availabilityList.forEach((productAvailabilities) => {
      productAvailabilities.forEach((productAvailability) => {
        const product = uniqueProducts.get(productAvailability.id);
        if (!product) {
          return;
        }

        product.availabilities = (product.availabilities ?? []).concat(productAvailability.availabilities || []);
        product.sessions = (product.sessions ?? []).concat(productAvailability.sessions || []);
      });
    });

    return Array.from(uniqueProducts.values());
  }

  private createOrderItemFromBookingItem(
    booking: IUpcomingBookingDetails,
    item: IBookingItem,
    orderItems: IOrderItem[],
    parentProduct: IProduct | undefined
  ) {
    if (!parentProduct || !item.product) {
      this.loggerService.warn('Building order for guest, unable to find product in current list of products', {
        message: item.bookingItemId.toString()
      });
      return;
    }

    const sessions = (parentProduct.packageSessions || parentProduct.sessions) as TWhenAttendingProductSession[];
    let matchingSession: TWhenAttendingProductSession | undefined;

    if (item.sessionStart && item.sessionName && !!sessions) {
      matchingSession = sessions.find(
        (session: TWhenAttendingProductSession) =>
          session.date === item.bookingDate && session.name === item.sessionName
      );
    }

    const selectedSession = this.whenAttendingSessionService.getSelectedSession(matchingSession, item.product);
    const ticketQuantity = item.quantity * item.product.groupSize + item.additionalGuests;
    const increment: INumberIncrementProductItem = {
      id: item.product.id,
      quantity: item.quantity,
      product: item.product,
      ticketQuantity: ticketQuantity,
      additionalGuests: item.additionalGuests
    };

    let orderItem: IOrderItem;

    // forced addons
    if (item.associatedBookingItemId) {
      const isForcedPurchase = this.productHelperService.isForcedPurchaseAddon(parentProduct);
      const associatedProduct = booking?.bookingItems.find(
        (bookingItem: IBookingItem) => bookingItem.bookingItemId === item.associatedBookingItemId
      );
      const associatedBasketItem = orderItems.find(
        (orderItem: IOrderItem) =>
          orderItem.productId === associatedProduct?.productId?.toString() &&
          orderItem.relatedOrderItemKey === associatedProduct?.relatedOrderItemKey
      );

      orderItem = this.orderItemService.buildAddonOrderItem(
        increment,
        parentProduct,
        this.dateFormatService.dateStringToDate(item.bookingDate),
        selectedSession || associatedBasketItem?.session,
        associatedBasketItem,
        isForcedPurchase,
        item.relatedOrderItemKey || associatedBasketItem?.relatedOrderItemKey
      );
    } else {
      orderItem = this.orderItemService.buildOrderItem(
        increment,
        parentProduct,
        this.dateFormatService.dateStringToDate(item.bookingDate),
        selectedSession,
        [],
        item.relatedOrderItemKey
      );

      orderItem.orderSelections = this.getPartyIncludedVariations(item, parentProduct);
      orderItem.optionSetSelections = this.getPackageIncludedVariations(item, parentProduct);
      orderItem.isCompulsoryPurchase = item.product.forceMinPurchase;
    }

    return orderItem;
  }

  private getPartyIncludedVariations(bookingItem: IBookingItem, parentProduct: IProduct) {
    if (parentProduct.type !== 'partypackage' || !bookingItem.packageBookingItems) {
      return [];
    }

    return bookingItem.packageBookingItems.map((packageItem) => {
      return this.orderItemService.mapToOrderSelections(
        packageItem.parentProductId,
        packageItem.productId,
        packageItem.quantity,
        packageItem.name,
        packageItem.description
      );
    });
  }

  private getPackageIncludedVariations(bookingItem: IBookingItem, parentProduct: IProduct) {
    if (parentProduct.type !== 'package' || !bookingItem.packageBookingItems) {
      return [];
    }

    return bookingItem.packageBookingItems
      .filter((x) => x.productPackageOptionSetId)
      .map((packageItem) => {
        const productOptionSetName = bookingItem.product?.packageOptionSets?.find(
          (x) => x.productPackageOptionSetId === packageItem.productPackageOptionSetId?.toString()
        )?.name;
        return this.orderItemService.mapToOptionSetSelections(
          packageItem.productPackageOptionSetId!,
          packageItem.productId,
          packageItem.quantity,
          productOptionSetName,
          packageItem.description
        );
      });
  }

  /**
   * The meta CreateSeparateItemsForPackages (mainly Skyline) creates a separate line entry for every package item ticket that is purchased
   * The method finds all the related items and merges them together with an array of bookingItemId
   */
  private mergeSeparateItemsForPackages(orderItems: IOrderItem[]): IOrderItem[] {
    return orderItems.reduce((acc: IOrderItem[], item: IOrderItem) => {
      if (item.productType !== 'package') {
        acc.push(item);
        return acc;
      }

      const relatedOrderItem = acc.find((currentItem) => this.orderItemService.isOrderItemMatch(item, currentItem));
      if (relatedOrderItem?.existingBookingItem?.bookingItemId && item.existingBookingItem?.bookingItemId) {
        relatedOrderItem.quantity += 1;
        relatedOrderItem.existingBookingItem.quantity = (relatedOrderItem.existingBookingItem.quantity ?? 0) + 1;
        relatedOrderItem.existingBookingItem.bookingItemIds ??= [relatedOrderItem.existingBookingItem.bookingItemId];
        relatedOrderItem.existingBookingItem.bookingItemIds.push(item.existingBookingItem.bookingItemId);
        relatedOrderItem.optionSetSelections = this.mergeOptionSetSelections(
          (relatedOrderItem.optionSetSelections || []).concat(item.optionSetSelections || [])
        );
      } else {
        acc.push(item);
      }

      return acc;
    }, []);
  }

  private mergeOptionSetSelections(optionSetSelections: IOptionSetSelection[]) {
    const optionSetSelectionDict: { [key: string]: IOptionSetSelection } = {};

    for (const optionSetSelection of optionSetSelections) {
      const key = `${optionSetSelection.productPackageOptionSetId}-${optionSetSelection.productId}`;
      const existingOptionSetSelection = optionSetSelectionDict[key];
      if (existingOptionSetSelection) {
        existingOptionSetSelection.quantity += optionSetSelection.quantity;
      } else {
        optionSetSelectionDict[key] = optionSetSelection;
      }
    }

    return Object.values(optionSetSelectionDict);
  }

  /** Reset current product availability list and load all required for existing booking dates */
  private async loadProductWithAvailabilities(booking: IUpcomingBookingDetails): Promise<IProduct[]> {
    this.productService.clearProductAvailability();
    const getProductAvailabilities = [
      ...new Set(booking?.bookingItems.map((booking: IBookingItem) => booking.bookingDate))
    ].map((bookingDate: string) => this.productService.getProductAvailability(bookingDate, booking?.bookingUniqueId));

    const products: IProduct[][] = await firstValueFrom(combineLatest(getProductAvailabilities));
    let merged = await this.mergeProducts(products);
    merged = this.venueConfigHelperService.addProductTypesToTickets(merged);
    merged = this.venueConfigHelperService.addProductsToPartyPackageItems(merged);
    merged = this.venueConfigHelperService.addProductsToProductOptionSets(merged);

    return merged;
  }
}
